import { ReactNode, useEffect, useState } from 'react';

import { Radio } from '@sorare/core/src/atoms/inputs/Radio';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CreditCardLabel } from '@sorare/core/src/components/creditCard/CreditCardLabel';
import { CreditCardIcon } from '@sorare/core/src/components/paymentMethods/Icon/CreditCardIcon';
import { usePreferredPaymentMethod } from '@sorare/core/src/hooks/wallets/usePreferredPaymentMethod';
import {
  OrderedPaymentMethod,
  PaymentMethod as PaymentMethodEnun,
} from '@sorare/core/src/lib/paymentMethod';

import { OptionConfig } from 'components/buyActions/Context';

export const useGroupedCreditCardsOption = (
  creditCardOptions: OptionConfig[],
  loading: boolean,
  topUpWalletNode?: ReactNode
): OptionConfig => {
  const { preferredOrderedPaymentMethod } = usePreferredPaymentMethod();
  const [selectedCreditCardOption, setSelectedCreditCardOption] =
    useState<OrderedPaymentMethod>();

  useEffect(() => {
    if (
      !loading &&
      (!selectedCreditCardOption ||
        !creditCardOptions.map(o => o.value).includes(selectedCreditCardOption))
    ) {
      setSelectedCreditCardOption(
        preferredOrderedPaymentMethod &&
          creditCardOptions
            .map(o => o.value)
            .includes(preferredOrderedPaymentMethod)
          ? preferredOrderedPaymentMethod
          : creditCardOptions[0].value
      );
    }
  }, [
    loading,
    selectedCreditCardOption,
    preferredOrderedPaymentMethod,
    creditCardOptions,
  ]);

  const selectedCreditCardOptionConfig = creditCardOptions.find(
    option => selectedCreditCardOption === option.value
  );

  return {
    value: PaymentMethodEnun.CREDIT_CARD,
    Icon: CreditCardIcon,
    labelContent: <CreditCardLabel />,
    disabled: selectedCreditCardOptionConfig?.disabled,
    onSubmit: async () => {
      if (selectedCreditCardOptionConfig) {
        selectedCreditCardOptionConfig.onSubmit?.();
      }
    },
    loading: selectedCreditCardOptionConfig?.loading,
    buttonProps: {
      disabled:
        !selectedCreditCardOptionConfig ||
        selectedCreditCardOptionConfig.buttonProps?.disabled,
    },
    getFees: selectedCreditCardOptionConfig?.getFees,
    topUpWalletWithCC: selectedCreditCardOptionConfig?.topUpWalletWithCC,
    button: selectedCreditCardOptionConfig?.button,
    pm: selectedCreditCardOptionConfig?.pm,
    Content: ({ isSelected }) => {
      if (creditCardOptions.length === 1)
        return (
          <>
            {topUpWalletNode}
            <Vertical>
              {selectedCreditCardOptionConfig?.Content?.({
                isSelected: true,
              })}
            </Vertical>
          </>
        );
      return (
        <Vertical>
          {topUpWalletNode}
          <Collapse open={isSelected} hideGap={!!topUpWalletNode}>
            <Vertical>
              {creditCardOptions.map(option => (
                <Radio
                  key={option.value}
                  onChange={() => {
                    setSelectedCreditCardOption(option.value);
                  }}
                  labelContent={option.labelContent}
                  disabled={option.disabled}
                  name={option.value}
                  value={option.value}
                  checked={option.value === selectedCreditCardOption}
                  checkedColor="var(--c-brand-600)"
                  reverse
                />
              ))}
            </Vertical>
            {selectedCreditCardOptionConfig?.Content?.({
              isSelected: true,
            })}
          </Collapse>
        </Vertical>
      );
    },
  };
};
